import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { authedFetch } from '../../utils/authedFetch';

import {
  Button,
  Typography,
  Stack,
  Chip,
  Alert,
  Paper,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CircularProgress from '@mui/material/CircularProgress';

import PatientDemosForm from './PatientDemographicsForm';
import PatientHistory from './PastHistory';
import DocumentManager from './DocumentManager';
import CreateBloodResults from './EPRBloodResults';
import FacilitatorObsInput from './EPRObs';
import ObservationViewerEditor from './ObsEditor';
import FacilitatorMedChart from './EPRMeds';
import AllergiesAlerts from './EPRAllergies';
import PatientRadiology from './PatientRadiology';
import { makeId } from '../../utils/generateid';
import { Modal, Box } from '@mui/material';
import FeedbackButton from '../../utils/feedbackButton';
import dayjs from 'dayjs';

const steps = [
{
    header: 'Patient/EPR data',
    items: [
      'Patient',
      'History',
      'Documentation',
      'Blood Results',
      'Imaging',
      'Observations',
      'Medications',
      'Allergies/Alerts'
    ],
  },
];

export default function PatientWizardMain() {
  const API_BASE = process.env.REACT_APP_API_URL;
  const { user, session, authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoadingPatientData, setIsLoadingPatientData] = useState(false)
  const [patientSaved, setPatientSaved] = useState(false);
  const [savingPatient, setSavingPatient] = useState(false);
  const [saveStatus, setSaveStatus] = useState({});
  const [scenarioId, setScenarioId] = useState(null);
  const [scenarioData, setScenarioData] = useState({
    patient: {
      name: '',
      dob: '',
      sex: '',
    },                     
    medicalhx: [],         
    surghx: [],   
    allergies: [],
    alerts: [],        
    eprDocs: [],
    eprBloods: [],
    eprImaging: [],
    eprObs: [],
    medications: [],
    scenariodetails: [],
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const flatSteps = steps.flatMap(group => group.items);
  const currentStepName = flatSteps[currentStep];

  const unsavedSteps = useMemo(
    () => flatSteps.filter(s => saveStatus[s] !== 'saved'),
    [flatSteps, saveStatus]
  );

  const hasPatientId = !!scenarioData.patient?.id;
  const flow = location.state?.flow ?? 'scenario-create';
  const patientId = location.state?.patientId ?? null;
  const allEprSaved = hasPatientId && unsavedSteps.length === 0;
  
  useEffect(() => {
    const sidFromState = location.state?.scenarioId;
    const sidFromStorage = localStorage.getItem('scenarioId');
    const sid = sidFromState ?? sidFromStorage ?? null;
    if (sid != null) setScenarioId(Number(sid));
  }, [location.state]);

  const handleNextPage = async() => {
    setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
  };

  // Saves data and moves to next screen
  const handleNextSave = async () => {
    if (currentStepName === 'Patient') {
      const { patient } = scenarioData;

      if (!patient.name || !patient.dob || !patient.sex) {
        alert('Patient name, DOB, and sex are required.');
        return;
      }

      setSavingPatient(true);

      try {
        const userid = 1; // Replace with actual user ID from context/session
        const name = patient.name;
        const query = new URLSearchParams({ name, userid }).toString();
        const res = await authedFetch({
          accessToken: session.access_token,
          apiBase: API_BASE,
          path: `/api/patients?${query}`,
          navigate,
        });
        
        const existing = await res.json();

        let patient_id;

        if (existing.exists) {
            alert('Patient already exists. Please change the patient name to proceed.');
            setSavingPatient(false);
            return;
        } else {
          // Create new patient
          const savePatient = await authedFetch({
            accessToken: session.access_token,
            apiBase: API_BASE,
            path: `/api/patients`,
            navigate,
            options: {
              method: 'POST',
              body: JSON.stringify({
                name: patient.name,
                dob: patient.dob,
                sex: patient.sex,
                }),
            },
          })

          const created = await savePatient.json();
          patient_id = created.id;
        }

        // Save patient ID to scenarioData
        setScenarioData((prev) => ({
          ...prev,
          patient: {
            ...prev.patient,
            id: patient_id
          }
        }));
        setSavingPatient(false);
        setPatientSaved(true);
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving patient:', err);
        alert('Failed to save patient data.');
        setSavingPatient(false);
      }

    } else if (currentStepName === 'History') {
      const { medicalhx, surghx, patient } = scenarioData;
      const patient_id = patient?.id;

      if (!patient_id) {
        alert('No patient ID found. Please complete the Patient step first.');
        return;
      }

      setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/medicalhx/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/surgicalhx/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        await Promise.all(
          medicalhx.map((hx) =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: '/api/medicalhx',
              navigate,
              options: {
                method: 'POST',
                body: JSON.stringify({
                  patient_id,
                  medicalhx: hx,
                }),
              },
            })
          )
        );

        await Promise.all(
          surghx.map((entry) =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: '/api/surgicalhx',
              navigate,
              options: {
                method: 'POST',
                body: JSON.stringify({
                  patient_id,
                  surgery: entry.surgery,
                  surgery_date: entry.date,
                }),
              },
            })
          )
        );

        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Failed to save history:', err);
        alert('Failed to save history data.');
        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'unsaved' }));
      }

    } else if (currentStepName === 'Documentation') {
      const { patient, eprDocs } = scenarioData;
      const patient_id = patient?.id;

      if (!patient_id) {
        alert('No patient ID found. Please complete the Patient step first.');
        return;
      }

      setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/documents/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // Save each document
        await Promise.all(
          eprDocs.map((doc) =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: `/api/documents`,
              navigate,
              options: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: doc.id,
                  patient_id,
                  title: doc.title,
                  content: doc.content,
                  type: doc.type,
                  date: doc.date,
                  time_offset: doc.time_offset,
                  time_offset_unit: doc.time_offset_unit,
                }),
              }
            })
          )
        );

        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving documents:', err);
        alert('Failed to save documentation.');
        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'unsaved' }));
      }

    } else if (currentStepName === 'Blood Results') {
      const { patient, eprBloods } = scenarioData;
      const patientId = patient.id;

      const bloodSets = Array.isArray(eprBloods)
        ? eprBloods
        : [];

      if (!patientId) {
        alert('You must save patient demographics before saving blood results.');
        return;
      }

      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // 1. Clear previous bloods
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/bloods/${patientId}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // 2. Save each blood set
        for (const bloodSet of bloodSets) {
          await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/save-bloods`,
            navigate,
            options: {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                patientId,
                timestamp: bloodSet.timestamp,
                offset: bloodSet.time_offset,
                offsetunits: bloodSet.time_offset_unit,
                data: Object.fromEntries(
                  bloodSet.groups.map(group => [
                    group.label,
                    group.values.map(test => ({
                      name: test.name,
                      value: test.value,
                      units: test.units,
                      refLow: test.refLow,
                      refHigh: test.refHigh,
                    }))
                  ])
                )
              })
            }
          });
        }

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving bloods:', err);
        alert('Failed to save blood results.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
      }

    } else if (currentStepName === 'Observations') {
      const { patient, eprObs } = scenarioData;
      const patient_id = patient.id;

      if (!patient_id) {
        alert('You must save patient demographics before saving observations.');
        return;
      }

      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // 1. Delete previous observations
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/observations/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // 2. Save each observation
        for (const obs of eprObs) {
          const clean = (value) => value === '' ? null : value;

          await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/saveobs`,
            navigate,
            options: {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                patient_id,
                datetime: dayjs(),
                offset: obs.time_offset,
                offsetunits: obs.time_offset_unit,
                rr: clean(obs.rr),
                spo2: clean(obs.spo2),
                sbp: clean(obs.sbp),
                pulse: clean(obs.pulse),
                temp: clean(obs.temp),
                avpu: obs.avpu || null,
                air: obs.air || null,
                o2: clean(obs.o2),
                device: obs.device || null
              })
            }
            });
        }

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving observations:', err);
        alert('Failed to save observations.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
      }

      
    } else if (currentStepName === 'Medications') {
      const { medications, patient } = scenarioData;
      const patient_id = patient.id;

      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // Clear existing meds
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/medications/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // Save each medication
        await Promise.all(
          medications.map(med =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: `/api/medications`,
            navigate,
            options: {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ patient_id, ...med })
            }})
          )
        );

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving medications:', err);
        alert('Failed to save medications.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
      }
    } else if (currentStepName === 'Scenario Details') {
      const { scenariodetails, patient } = scenarioData;
      const patient_id = patient.id;

      if (!scenariodetails.title || !scenariodetails.description || !scenariodetails.vignette || !scenariodetails.complaint) {
        alert('Missing scenario details. Title, description, vignette, and patient complaint required.');
        return;
      }

      setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        const userid = 1; // Replace with actual user ID from context/session
        const title = scenariodetails.title;
        const query = new URLSearchParams({ title, userid }).toString();

        const res = await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/scenarios?${query}`,
        })

        const existing = await res.json();

        let scenario_id;

        if (existing.exists) {
            alert('Scenario already exists. Please change the scenario title to proceed.');
            return;
        } else {
          // Create new scenario
        const saveScenario = await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: `/api/scenarios`,
          navigate,
          options: {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: scenariodetails.title,
            description: scenariodetails.description,
            vignette: scenariodetails.vignette,
            complaint: scenariodetails.complaint,
            time: scenariodetails.time,
            user_id: userid,
            patient_id: patient_id,
          }),
          }})

          const created = await saveScenario.json();
          scenario_id = created.id;
        }

        setScenarioData((prev) => ({
          ...prev,
          scenariodetails: {
            ...prev.scenariodetails,
            id: scenario_id
          }
        }));

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
      } catch (err) {
        console.error('Error saving scenario:', err);
        alert('Failed to save scenario.');
        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'unsaved' }));
      };
    } else if (currentStepName === 'Allergies/Alerts') {
      const { allergies, alerts, patient } = scenarioData;
      const patient_id = patient?.id;

      if (!patient_id) {
        alert('No patient ID found. Please complete the Patient step first.');
        return;
      }

      setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/allergies/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/alerts/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // Save new allergy history
        await Promise.all(
          allergies.map((allergy) =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: `/api/allergies`,
              navigate,
              options: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patient_id, allergy:allergy })
              }
            })
          )
        );

        // Save new alert history
        await Promise.all(
          alerts.map((entry) =>
            authedFetch({
              accessToken: session?.access_token,
              apiBase: API_BASE,
              path: `/api/alerts`,
              navigate,
              options: {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patient_id, alert: entry }),                
              }
            })
          )
        );

        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving history:', err);
        alert('Failed to save history data.');
        setSaveStatus((prev) => ({ ...prev, [currentStepName]: 'unsaved' }));
      }
    
    } else if (currentStepName === 'Imaging') {
      const { patient, eprImaging } = scenarioData;
      const patient_id = patient?.id;

      if (!patient_id) {
        alert('You must save patient demographics before saving radiology.');
        return;
      }

      setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saving' }));

      try {
        // Clear previous saved imaging for this patient
        await authedFetch({
          accessToken: session?.access_token,
          apiBase: API_BASE,
          path: `/api/images/${patient_id}`,
          navigate,
          options: {
            method: 'DELETE',
          },
        });

        // Save each selected image
        for (const img of eprImaging) {
          await authedFetch({
            accessToken: session?.access_token,
            apiBase: API_BASE,
            path: '/api/save-images',
            navigate,
            options: {
              method: 'POST',
              body: JSON.stringify({
                patient_id,

                image_id: img.image_id,
                image_source: img.image_source || 'public',
                library_key: img.library_key || `${img.image_source || 'public'}:${img.image_id}`,

                bucket: img.bucket,
                file_path: img.file_path,
                title: img.title,
                modality: img.modality,
                study_type: img.study_type || null,
                body_part: img.body_part || null,
                preview_label: img.preview_label || null,
                description: img.description || null,

                report_visible: !!img.report_visible,
                report: img.report || null,
                time_offset:
                  img.time_offset === '' || img.time_offset == null
                    ? 0
                    : Number(img.time_offset),
                time_offset_unit: img.time_offset_unit || 'hours',
              }),
            },
          });
        }

        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'saved' }));
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      } catch (err) {
        console.error('Error saving radiology:', err);
        alert('Failed to save radiology.');
        setSaveStatus(prev => ({ ...prev, [currentStepName]: 'unsaved' }));
      }    
    } else {
        // Proceed normally for other steps
        setCurrentStep((s) => Math.min(s + 1, flatSteps.length - 1));
      }
    };

  // Moves on to edit scenario
  const handleGoToScenario = () => {
    if (!hasPatientId) return;

    if (unsavedSteps.length > 0) {
      const proceed = window.confirm(
        `These EPR sections are not saved:\n\n• ${unsavedSteps.join(
          '\n• '
        )}\n\nScenario setup expects EPR to be complete. Continue anyway?`
      );
      if (!proceed) return;
    }

    navigate('/app/create/scenario', {
      state: { patientId: scenarioData.patient.id, patientName: scenarioData.patient.name, scenarioId: scenarioId }
    });
    
  };

  // ---- ON MOUNT / RETURN ----
useEffect(() => {
  const stateId = location.state?.patientId ?? null;
  const patientId = stateId ?? null;

  if (authLoading) return;
  if (!session || !user) return;
  if (!patientId || scenarioData.patient?.id) return;

  (async () => {
    try {
      setIsLoadingPatientData(true);

      const res = await authedFetch({
        accessToken: session.access_token,
        apiBase: API_BASE,
        path: `/api/full-patient/${patientId}`,
        navigate,
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const full = await res.json();

      const eprBloods = (full.eprBloods ?? []).map((set) => ({
        ...set,
        id: set.id ?? makeId(),
      }));

      setScenarioData((prev) => ({
        ...prev,
        patient: {
          id: full.patient.id,
          name: full.patient.name,
          dob: full.patient.dob,
          sex: full.patient.sex,
        },
        medicalhx: full.medicalhx ?? [],
        surghx: full.surghx ?? [],
        eprDocs: full.eprDocs ?? [],
        eprBloods,
        eprObs: full.eprObs ?? [],
        eprImaging: full.eprImaging ?? [],
        medications: full.medications ?? [],
        allergies: full.allergies ?? [],
        alerts: full.alerts ?? [],
      }));

      setPatientSaved(true);
      setSaveStatus((prev) => ({
        ...prev,
        Patient: 'saved',
        History: 'saved',
        Documentation: 'saved',
        'Blood Results': 'saved',
        Observations: 'saved',
        Imaging: 'saved',
        Medications: 'saved',
        'Allergies/Alerts': 'saved',
      }));
    } catch (e) {
      console.error('Failed to hydrate EPR from patientId:', e);
    } finally {
      setIsLoadingPatientData(false);
    }
  })();
}, [
  authLoading,
  session,
  user,
  navigate,
  API_BASE,
  location.state,
  scenarioData.patient?.id,
]);

const currentStatus = saveStatus[currentStepName];
const isCurrentStepSaved = currentStatus === 'saved';
const isCurrentStepSaving = currentStatus === 'saving' || savingPatient;
const isLastStep = currentStep === flatSteps.length - 1;

const primaryButtonLabel = isCurrentStepSaved
  ? isLastStep
    ? 'Finish EPR'
    : 'Next'
  : isLastStep
  ? 'Save'
  : 'Save & next';

const primaryButtonIcon = isCurrentStepSaved ? (
  <ArrowForwardIcon />
) : (
  <SaveOutlinedIcon />
);

  return (
    <div style={{ display: 'flex', height: '100%' }}>
        {/* Sidebar Navigation */}
        <Box
          sx={{
            width: 250,
            borderRight: '1px solid #E5E7EB',
            p: 2,
            bgcolor: 'white',
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}
          >
            <Typography sx={{ fontWeight: 800 }}>
              EPR data
            </Typography>

            <Button
              size="small"
              variant={allEprSaved ? 'contained' : 'outlined'}
              color={allEprSaved ? 'success' : 'warning'}
              onClick={handleGoToScenario}
              disabled={!hasPatientId}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              Scenario
            </Button>
          </Box>

          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {steps.map((group, groupIdx) => (
              <li key={groupIdx}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {group.items.map((step) => {
                    const stepIndex = flatSteps.indexOf(step);
                    const status = saveStatus[step];
                    const isActive = currentStep === stepIndex;
                    const isSaved = status === 'saved';
                    const isSaving = status === 'saving';
                    const isLocked = !saveStatus['Patient'] && stepIndex !== 0;

                    return (
                      <li
                        key={stepIndex}
                        onClick={() => {
                          if (isLocked) {
                            alert('You must complete and save patient information before continuing.');
                            return;
                          }

                          setCurrentStep(stepIndex);
                        }}
                        style={{
                          listStyle: 'none',
                          marginBottom: 6,
                          opacity: isLocked ? 0.45 : 1,
                          cursor: isLocked ? 'not-allowed' : 'pointer',
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            px: 1.25,
                            py: 0.9,
                            borderRadius: 2,
                            bgcolor: isActive ? '#EEF2FF' : 'transparent',
                            border: isActive
                              ? '1px solid #BFDBFE'
                              : '1px solid transparent',
                            transition: '0.15s ease',
                            '&:hover': {
                              bgcolor: isActive ? '#EEF2FF' : '#F8FAFC',
                              borderColor: isActive ? '#BFDBFE' : '#E5E7EB',
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 8,
                              bottom: 8,
                              width: 3,
                              borderRadius: 99,
                              bgcolor: isActive ? '#2563EB' : 'transparent',
                            },
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.9rem',
                                fontWeight: isActive ? 800 : 600,
                                color: isLocked ? 'text.disabled' : 'text.primary',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {step}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: 18,
                              height: 18,
                              borderRadius: '50%',
                              border: isSaved
                                ? '1px solid #16A34A'
                                : isSaving
                                ? '1px solid #60A5FA'
                                : '1px solid #CBD5E1',
                              bgcolor: isSaved
                                ? '#DCFCE7'
                                : isSaving
                                ? '#DBEAFE'
                                : '#F8FAFC',
                            }}
                            title={isSaved ? 'Saved' : isSaving ? 'Saving' : 'Unsaved'}
                          >
                            {isSaving ? (
                              <CircularProgress size={10} />
                            ) : (
                              <Box
                                sx={{
                                  width: 7,
                                  height: 7,
                                  borderRadius: '50%',
                                  bgcolor: isSaved ? '#16A34A' : '#CBD5E1',
                                }}
                              />
                            )}
                          </Box>
                        </Box>
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>

          <Box sx={{ mt: 2 }}>
            <Button
              fullWidth
              variant="contained"
              startIcon={
                isCurrentStepSaving ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  primaryButtonIcon
                )
              }
              onClick={() => {
                if (!isCurrentStepSaved) {
                  handleNextSave();
                } else {
                  handleNextPage();
                }
              }}
              disabled={isCurrentStepSaving || (currentStep > 0 && patientSaved === false)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 2,
                py: 1,
              }}
            >
              {isCurrentStepSaving ? 'Saving…' : primaryButtonLabel}
            </Button>

            {!isCurrentStepSaved && !isCurrentStepSaving && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 0.75, textAlign: 'center' }}
              >
                Saves this section before moving on.
              </Typography>
            )}
          </Box>
        </Box>
        
        <div style={{display:'flex', flexDirection:'column', width:'100%'}}>
        {/* Navigation Buttons */}
        <Paper
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            borderBottom: '1px solid #dbeafe',
            bgcolor: 'rgba(255,255,255,0.96)',
            backdropFilter: 'blur(10px)',
            px: 2,
            py: 1.5,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography sx={{ fontWeight: 800 }}>
                  {currentStepName}
                </Typography>

                {isCurrentStepSaved ? (
                  <Chip
                    size="small"
                    icon={<CheckCircleOutlineIcon />}
                    label="Saved"
                    color="success"
                    variant="outlined"
                  />
                ) : isCurrentStepSaving ? (
                  <Chip
                    size="small"
                    label="Saving…"
                    color="info"
                    variant="outlined"
                  />
                ) : (
                  <Chip
                    size="small"
                    icon={<WarningAmberOutlinedIcon />}
                    label="Unsaved changes"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>

              {!isCurrentStepSaved && !isCurrentStepSaving && (
                <Typography variant="body2" color="text.secondary">
                  Save this section before moving on, otherwise your changes may not be stored.
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {currentStep > 0 && (
                <Button
                  variant="outlined"
                  onClick={() => setCurrentStep((s) => Math.max(s - 1, 0))}
                  disabled={isCurrentStepSaving}
                  sx={{ textTransform: 'none' }}
                >
                  Back
                </Button>
              )}

              <Button
                variant="contained"
                startIcon={
                  isCurrentStepSaving ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    primaryButtonIcon
                  )
                }

                onClick={() => {
                  if (!isCurrentStepSaved) {
                    handleNextSave();
                  } else {
                    handleNextPage();
                  }
                }}
                disabled={
                  isCurrentStepSaving ||
                  (currentStep > 0 && patientSaved === false)
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  minWidth: 140,
                  borderRadius: 2,
                }}
              >
                {isCurrentStepSaving ? 'Saving…' : primaryButtonLabel}
              </Button>
            </Stack>
          </Stack>
        </Paper>
        
        <div className='createcontent' style={{ flex: 1, padding: '1rem' }}>
            <div style={{ marginTop: '1rem' }}>

              {currentStepName === 'Patient' && savingPatient && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <CircularProgress size={20} />
                  <span>Saving patient...</span>
                </div>
              )}

              { saveStatus[currentStepName] === 'saving' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                  <CircularProgress size={20} />
                  <span>Saving EPR data...</span>
                </div>
              )}

              {/* Patient Demos */}
              {currentStepName === 'Patient' && !savingPatient && (
                <PatientDemosForm
                  key={scenarioData.patient?.id ?? 'new'}
                  flow={flow}
                  data={{ patient: scenarioData.patient }}
                  onChange={(newData, options) => {
                    const merged = {
                      ...scenarioData,
                      ...newData,
                    };

                    setScenarioData(merged);

                    const newSaveStatus = { ...saveStatus };

                    if (options?.markSaved) {
                      setPatientSaved(true);
                      newSaveStatus['Patient'] = 'saved';
                    } else {
                      newSaveStatus['Patient'] = 'unsaved';
                    }

                    if (options?.markOtherStepsSaved) {
                      newSaveStatus['History'] = 'saved';
                      newSaveStatus['Documentation'] = 'saved';
                      newSaveStatus['Blood Results'] = 'saved';
                      newSaveStatus['Observations'] = 'saved';
                      newSaveStatus['Medications'] = 'saved';
                      newSaveStatus['Allergies/Alerts'] = 'saved';
                    }

                    setSaveStatus(newSaveStatus);
                  }}
                />
              )}
              
              {/* Patient History */}
              {currentStepName === 'History' && saveStatus[currentStepName] !== 'saving' && (
                <PatientHistory
                  data={{
                    medicalhx: scenarioData.medicalhx,
                    surghx: scenarioData.surghx
                  }}

                  onChange={({ medicalhx, surghx }) => {
                    const prev = JSON.stringify({
                      medicalhx: scenarioData.medicalhx,
                      surghx: scenarioData.surghx
                    });

                    const next = JSON.stringify({ medicalhx, surghx });

                    if (prev !== next) {
                      setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }));
                    }

                    setScenarioData((prev) => ({
                      ...prev,
                      medicalhx,
                      surghx
                    }))
                  }}
                />
              )}

              {/* Patient docs */}
              {currentStepName === 'Documentation' && saveStatus[currentStepName] !== 'saving' && (
                  <DocumentManager
                  documents={scenarioData.eprDocs}
                  setDocuments={(docs) => {
                    const prev = JSON.stringify(scenarioData.eprDocs)
                    const next = JSON.stringify(docs)

                    if (prev !== next) {
                      setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                    }

                    setScenarioData((prev) => ({ ...prev, eprDocs: docs }))
                  }}
                  />
              )}

              {/* Patient bloods */}
              {currentStepName === 'Blood Results' && saveStatus[currentStepName] !== 'saving' && (
                <CreateBloodResults
                  data={scenarioData.eprBloods}
                  onChange={(updated) => {
                    const prev = JSON.stringify(scenarioData.eprBloods)
                    const next = JSON.stringify(updated)

                    if (prev !== next) {
                      setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                    }

                    setScenarioData((prev) => ({ ...prev, eprBloods: updated }))
                  }}
                />
              )}

              {/* Patient meds */}
              {currentStepName === 'Medications' && saveStatus[currentStepName] !== 'saving' && (
                <FacilitatorMedChart
                  data={scenarioData.medications}
                  onChange={(updated) => {
                    setScenarioData((prev) => ({ ...prev, medications: updated }))
                    setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                  }
                  }
                />
              )}

              {/* Patient obs */}
              {currentStepName === 'Observations' && saveStatus[currentStepName] !== 'saving' && (
                <>
                <div>
                  <FacilitatorObsInput
                    data={scenarioData.eprObs}
                    onChange={(updated) => {
                      const prev = JSON.stringify(scenarioData.eprObs)
                      const next = JSON.stringify(updated)

                      if (prev !== next) {
                        setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                      }
                      setScenarioData((prev) => ({ ...prev, eprObs: updated }))}
                    }
                  />
                  <div style={{display:'flex', justifyContent:'center'}}>
                  <ObservationViewerEditor
                    data={scenarioData.eprObs}
                    onChange={(updated) => {
                      const prev = JSON.stringify(scenarioData.eprObs)
                      const next = JSON.stringify(updated)

                      if (prev !== next) {
                        setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }))
                      }
                      setScenarioData((prev) => ({ ...prev, eprObs: updated }))}
                    }
                  />
                  </div>
                  </div>
                </>
              )}

              {currentStepName === 'Imaging' && saveStatus[currentStepName] !== 'saving' && (
                <PatientRadiology
                  data={scenarioData.eprImaging}
                  patientId={scenarioData.patient?.id}
                  onChange={(updated) => {
                    const prev = JSON.stringify(scenarioData.eprImaging);
                    const next = JSON.stringify(updated);

                    if (prev !== next) {
                      setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }));
                    }

                    setScenarioData((prev) => ({ ...prev, eprImaging: updated }));
                  }}
                />
              )}

              {/* Allergies and Alerts */}
              {currentStepName === 'Allergies/Alerts' && saveStatus[currentStepName] !== 'saving' && (
                <AllergiesAlerts
                  data={{
                    allergies: scenarioData.allergies,
                    alerts: scenarioData.alerts
                  }}

                  onChange={({ allergies, alerts }) => {
                    const prev = JSON.stringify({
                      allergies: scenarioData.allergies,
                      alerts: scenarioData.alerts
                    });

                    const next = JSON.stringify({ allergies, alerts });

                    if (prev !== next) {
                      setSaveStatus(prevStatus => ({ ...prevStatus, [currentStepName]: 'unsaved' }));
                    }

                    setScenarioData((prev) => ({
                      ...prev,
                      allergies,
                      alerts
                    }))
                  }}
                />
              )}
            </div>
        </div>
        </div>

      <Modal open={isLoadingPatientData}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}>
          <CircularProgress />
          <Typography style={{ marginTop: '1rem' }}>Loading patient data...</Typography>
        </div>
      </Modal>

    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        left: 20,
        zIndex: 2000,
      }}
    >
      <FeedbackButton page="EPR Builder" />
    </Box>

    </div>
  );
}
